import { state } from "@angular/animations";
import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EMPTY } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ErrorType, IdStateModel } from "../../model/id.model";
import { FaceService } from "../../services/async/connectors/face.service";
import { setIdImageLoad } from "../actions/face.action";
import { idTimeOut, requestIdFaceDetection, setDefaultId, setIdFile } from "../actions/id.action";

@State<IdStateModel>({
    name: "id",
    defaults: {
        idImage: null,
        idFile: null,
        isLoaded: false,
        isLoading: true,
        isError: false,
        isImageLoading: false,
        timeOut: false,
        errorType: null,
    },
})
@Injectable({
    providedIn: "root",
})
export class IdState {

    constructor(private faceService: FaceService) { }

    @Selector()
    static getIdState(state: IdStateModel) {
        return state;
    }

    @Selector()
    static getIdImage(state: IdStateModel) {
        return state.idImage;
    }

    @Selector()
    static getIdFile(state: IdStateModel) {
        return state.idFile;
    }

    @Selector()
    static idTimeOut(state: IdStateModel) {
        return state.timeOut;
    }

    @Selector()
    static isLoaded(state: IdStateModel) {
        return state.isLoaded;
    }

    @Selector()
    static isLoading(state: IdStateModel) {
        return state.isLoading;
    }


    @Selector()
    static isError(state: IdStateModel) {
        return state.isError;
    }

    @Selector()
    static isQualityError(state: IdStateModel) {
        return state.errorType === ErrorType.UNSUFFICIANT_QUALITY;
    }

    @Selector()
    static isNoFaceDetectedError(state: IdStateModel) {
        return state.errorType === ErrorType.FACE_NOT_DETECTED;
    }

    @Selector()
    static isTooManyFacesDetectedError(state: IdStateModel) {
        return state.errorType === ErrorType.TOO_MANY_FACES;
    }

    @Selector()
    static isMediatypeError(state: IdStateModel) {
        return state.errorType === ErrorType.INVALID_MEDIA_TYPE;
    }

    @Selector()
    static isIdImageLoading(state: IdStateModel) {
        return state.isImageLoading;
    }

    @Action(setDefaultId)
    setDefaultId({ getState, patchState }: StateContext<IdStateModel>) {
        const state = getState();
        patchState({
            idImage: null,
            idFile: null,
            isLoaded: false,
            isLoading: true,
            isError: false,
            isImageLoading: false,
            timeOut: false,
        });
    }

    @Action(requestIdFaceDetection)
    getIdFaces(
        { getState, patchState }: StateContext<IdStateModel>,
        { payload }: requestIdFaceDetection
    ) {
        const state = getState();
        return this.faceService.detectFace(payload).pipe(
            map((face) => {
                if (face.length > 1) {
                    patchState({
                        ...state,
                        isLoaded: false,
                        isLoading: false,
                        isImageLoading: false,
                        isError: true,
                        errorType: ErrorType.TOO_MANY_FACES,
                        timeOut: false
                    });
                }
                else if (face.length < 1) {
                    patchState({
                        ...state,
                        isLoaded: false,
                        isLoading: false,
                        isImageLoading: false,
                        isError: true,
                        errorType: ErrorType.FACE_NOT_DETECTED,
                        timeOut: false
                    });
                }
                else {
                    const reader = new FileReader();
                    reader.addEventListener(
                        'load',
                        function () {
                            // convert image file to base64 string
                            let value: string = reader.result.toString();
                            let img = value.split(";base64,");
                            patchState({
                                ...state,
                                idFile: payload.idImage,
                                idImage: img[1],
                                isLoaded: true,
                                isLoading: false,
                                isImageLoading: false,
                                isError: false,
                                errorType: null,
                                timeOut: false
                            });
                        },
                        false
                    );
                    if (payload.idImage) {
                        reader.readAsDataURL(payload.idImage);
                    }

                }

            }),
            catchError((error) => {
                if (error.status === 0 || error.status === 401) {
                    patchState({
                        ...state,
                        isLoaded: false,
                        isLoading: true,
                        isImageLoading: false,
                        timeOut: true,
                    });
                    return EMPTY;
                } else if (error.status === ErrorType.FACE_NOT_DETECTED) {
                    patchState({
                        ...state,
                        isLoaded: false,
                        isLoading: false,
                        isImageLoading: false,
                        isError: true,
                        errorType: ErrorType.FACE_NOT_DETECTED,
                        timeOut: false,
                    });
                }
                else if (error.status === ErrorType.INVALID_MEDIA_TYPE) {
                    patchState({
                        ...state,
                        isLoaded: false,
                        isLoading: false,
                        isImageLoading: false,
                        isError: true,
                        errorType: ErrorType.INVALID_MEDIA_TYPE,
                        timeOut: false,
                    });
                }
            })
        );

    }



    @Action(setIdFile)
    setIdFile(
        { getState, patchState }: StateContext<IdStateModel>,
        { payload }: setIdFile
    ) {
        const state = getState();
        patchState({
            ...state,
            idFile: payload
        });
    }

    @Action(setIdImageLoad)
    setIdImageLoad(
        { getState, patchState }: StateContext<IdStateModel>,
        { payload }: setIdImageLoad
    ) {
        const state = getState();
        patchState({
            ...state,
            isError: false,
            errorType: null,
            isLoading: false,
            isImageLoading: payload,
        });
    }
    @Action(idTimeOut)
    setFaceTimeOut(
        { getState, patchState }: StateContext<IdStateModel>,
        { payload }: idTimeOut
    ) {
        const state = getState();
        patchState({ ...state, timeOut: payload });
    }

}